import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';
import type {VariantProps} from 'class-variance-authority';
import {cva} from 'class-variance-authority';

import {cn} from '@/utils/tailwind';

const button = cva(
	cn(
		'no-underline',
		'inline-flex',
		'items-center',
		'justify-center',
		'gap-2',
		'shrink-0',
		'rounded-xl',
		'transition-all',
		'border',
		'text-center',
		'leading-tight',
		'cursor-pointer',
	),
	{
		variants: {
			intent: {
				primary: [
					'active:bg-[var(--ds-color-base-active)]',
					'hover:bg-[var(--ds-color-base-hover)]',
					'bg-[var(--ds-color-base-default)]',
					'text-[var(--ds-color-base-contrast-default)]',
					'hover:text-[var(--ds-color-base-contrast-default)]',
					'border-transparent',
				],
				secondary: [
					'text-[var(--ds-color-text-subtle)]',
					'hover:text-[var(--ds-color-text-default)]',
					'border-[var(--ds-color-border-strong)]',
					'hover:bg-[var(--ds-color-surface-hover)]',
					'active:bg-[var(--ds-color-surface-hover)]',
				],
			},
			size: {
				small: ['px-4', 'py-1'],
				medium: ['px-6', 'py-3'],
				large: ['px-10', 'py-4'],
			},
		},
		compoundVariants: [{intent: 'primary', size: 'medium'}],
		defaultVariants: {
			intent: 'primary',
			size: 'medium',
		},
	},
);

export type ButtonProps = VariantProps<typeof button> &
	ComponentPropsWithoutRef<'button'> & {
		/**
		 * Change the default rendered element for the one passed as a child, merging their props and behavior.
		 * @default false
		 */
		asChild?: boolean;
	};

/**
 * Button used for interaction
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			intent = 'primary',
			size = 'medium',
			type = 'button',
			className,
			asChild,
			...rest
		},
		ref,
	) => {
		const Component = asChild ? Slot : 'button';

		return (
			<Component
				ref={ref}
				type={type}
				className={cn(
					button({intent, size}),
					className,
					'active:scale-[95%]',
				)}
				{...rest}
			>
				{children}
			</Component>
		);
	},
);

Button.displayName = 'Button';
